/**
 * What-If honesty strip (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md D5, §6): one calm warning-toned line per
 * user-named target that couldn't be applied, plus (if non-empty) one
 * line per coupled Input the patch-planning agent couldn't resolve at
 * all ("unattempted"). Renders nothing when both lists are empty — the
 * strip is not a fixed fixture of the proposal, only a fact when it
 * applies.
 *
 * Items are changes_log-shaped records (Agentic-API
 * app/ai/v2/cards/edit_scenarios_change_review.py::ChangeRecord) for
 * honesty, or UnAttemptedInput ({input_name, reason}) for unattempted.
 * Neither schema is contractually guaranteed field-for-field from this
 * side (types.ts's WhatIfHonestyItem is a loose index signature), so
 * every field read here is defensive against missing/undefined values —
 * a partially-populated record still renders a sentence, never a crash
 * or a blank line.
 */
import React from 'react';
import { Alert, Box } from '@mui/material';

import { WhatIfHonestyItem } from '../types';

interface HonestyStripProps {
  honesty: WhatIfHonestyItem[];
  unattempted: WhatIfHonestyItem[];
}

// Builds a human-readable "target" description from whatever filter
// fields are present, so the sentence names what the executive actually
// typed rather than just the input as a whole. Falls back gracefully
// when no filter fields are populated.
const describeTarget = (item: WhatIfHonestyItem): string => {
  const name =
    typeof item.input_name === 'string' && item.input_name
      ? item.input_name
      : 'That input';

  const filterBits: string[] = [];
  if (Array.isArray(item.column_filter) && item.column_filter.length > 0) {
    filterBits.push(item.column_filter.join(', '));
  }
  if (
    Array.isArray(item.parameter_filter) &&
    item.parameter_filter.length > 0
  ) {
    filterBits.push(item.parameter_filter.join(', '));
  }
  if (Array.isArray(item.filters_applied) && item.filters_applied.length > 0) {
    // filters_applied entries are free-form dicts (dimension -> value);
    // stringify defensively rather than assuming a shape.
    const rendered = item.filters_applied
      .map((f) => {
        if (f && typeof f === 'object') {
          const parts = Object.entries(f).map(([k, v]) => `${k}: ${String(v)}`);
          return parts.join(', ');
        }
        return String(f);
      })
      .filter(Boolean);
    filterBits.push(...rendered);
  }

  return filterBits.length > 0 ? `${name} (${filterBits.join(' · ')})` : name;
};

// Two distinct failure modes share the strip, and conflating them would
// misinform: a *_miss/no_row_match means the named target genuinely isn't
// in this model, whereas "apply_error" means the target WAS found but the
// write was refused (e.g. an input holding selector text rather than
// numbers). Wording is deliberately non-diagnostic for the latter — the
// strip's job is honesty, not explaining the model's internals, and
// inferring a cause from an exception string would risk being wrong.
const honestyMessage = (item: WhatIfHonestyItem): string => {
  if (item.result_class === 'apply_error') {
    return `This change couldn't be applied to '${describeTarget(item)}'.`;
  }
  return `'${describeTarget(item)}' wasn't found in this model — nothing was changed for it.`;
};

const unattemptedMessage = (item: WhatIfHonestyItem): string => {
  const name =
    typeof item.input_name === 'string' && item.input_name
      ? item.input_name
      : 'One of the requested changes';
  const reason =
    typeof item.reason === 'string' && item.reason ? ` ${item.reason}` : '';
  return `'${name}' couldn't be applied.${reason}`;
};

const HonestyStrip: React.FC<HonestyStripProps> = ({
  honesty,
  unattempted,
}) => {
  if (honesty.length === 0 && unattempted.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 2 }}>
      {honesty.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key -- items carry no stable id
        <Alert key={`honesty-${i}`} severity="warning" variant="outlined">
          {honestyMessage(item)}
        </Alert>
      ))}
      {unattempted.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key -- items carry no stable id
        <Alert key={`unattempted-${i}`} severity="warning" variant="outlined">
          {unattemptedMessage(item)}
        </Alert>
      ))}
    </Box>
  );
};

export default HonestyStrip;
